import React, { forwardRef, useEffect, useRef, useState } from "react";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as ChevronDownIcon } from "../../../icons/chevronDown.svg";
import {
  SelectContainer,
  StyledOptionsList,
  StyledOptionItemContainer,
  StyledOptionItem,
  StyledInputContainer,
  StyledSearchInputContainer,
  StyledLoaderEmptyContent,
  StyledOptionsScrollArea,
  StyledOptionItemLabelContainer,
} from "./Select.style";
import Input, { InputBorderEnum, InputSizes } from "../Input/Input";
import { BodyTyp } from "../../Typography/Typography";
import { RadioButton } from "../RadioButton/RadioButton";
import { Checkbox } from "../Checkbox/Checkbox";
import { theme } from "../../../constants/StyledComponents";
import SearchInput from "../SearchInput/SearchInput";
import { Loader } from "../Loader/Loader";
import { NO_RESULT_FOUND_LABEL } from "../../../constants/Dictionary";
import { ReactComponent as CheckIcon } from "../../../icons/check.svg";
import { createPortal } from "react-dom";
import { useMergedRefs } from "../../../hooks/useMergedRefs.hook";

export type SelectOption<T> = {
  value: T;
  label: string;
  description?: string;
};

export enum SelectVariantEnum {
  DEFAULT = "default",
  SINGLE_SELECT = "single_select",
  MULTI_SELECT = "multi_select",
}

type DefaultSelectProps<T> = {
  variant: SelectVariantEnum.DEFAULT;
  onChange?: (x: T | null) => void;
  value?: T | null;
};

type SingleSelectProps<T> = {
  variant: SelectVariantEnum.SINGLE_SELECT;
  onChange?: (x: T | null) => void;
  value?: T | null;
};

type MultiSelectProps<T> = {
  variant: SelectVariantEnum.MULTI_SELECT;
  onChange?: (x: T[]) => void;
  value?: T[];
  maxSelections?: number;
};

type SelectProps<T> = {
  options: SelectOption<T>[];
  label?: string;
  size?: InputSizes;
  onClear?: CallableFunction;
  allowClear?: boolean;
  disabled?: boolean;
  withSearch?: boolean;
  onSearch?: (text: string) => void;
  searchValue?: string;
  fetchMore?: CallableFunction;
  loading?: boolean;
  canLoadMore?: boolean;
  dropdownFixed?: boolean;
  fetchingMore?: boolean;
  error?: string;
  optionsContainerFitContent?: boolean;
  id?: string;
} & (DefaultSelectProps<T> | SingleSelectProps<T> | MultiSelectProps<T>);

function SelectInner<T>(
  {
    options,
    label,
    size = InputSizes.REGULAR,
    onChange,
    value,
    onClear,
    allowClear = true,
    variant,
    disabled = false,
    withSearch = false,
    onSearch,
    searchValue,
    fetchMore,
    loading,
    fetchingMore,
    canLoadMore,
    dropdownFixed = true,
    error,
    optionsContainerFitContent = false,
    id,
    ...props
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [isOpen, setIsOpen] = useState(false);

  const maxSelections =
    variant === SelectVariantEnum.MULTI_SELECT
      ? (props as MultiSelectProps<T>).maxSelections
      : undefined;

  const dropdownRef = useRef<HTMLUListElement>(null);
  const scrollOptionsRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    flipUp: false,
  });
  const [cachedSelectedOption, setCachedSelectedOption] = useState<
    SelectOption<T>[]
  >([]);
  //calculate position
  const inputRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleOptionClick = (option: SelectOption<T>) => {
    if (variant === SelectVariantEnum.MULTI_SELECT) {
      const copy = value ? [...value] : [];
      const item = copy.findIndex((x) => x === option.value);
      let newSelected: T[] = [];
      if (item >= 0) {
        newSelected = copy.filter((x) => x !== option.value);
        if (withSearch && onSearch) {
          setCachedSelectedOption((prev) =>
            prev.filter((x) => x.value !== option.value),
          );
        }
      } else {
        if (maxSelections && copy.length >= maxSelections) {
          return;
        }
        newSelected = [...copy, option.value];
        if (withSearch && onSearch) {
          setCachedSelectedOption((prev) => [...prev, option]);
        }
      }

      if (onChange) {
        onChange(newSelected);
      }
    } else if (
      variant === SelectVariantEnum.SINGLE_SELECT ||
      variant === SelectVariantEnum.DEFAULT
    ) {
      if (onChange) {
        onChange(option.value);
      }
      setIsOpen(false);

      if (withSearch && onSearch) {
        setCachedSelectedOption(() => [option]);
        onSearch("");
      }
    }
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const mergedRef = useMergedRefs(wrapperRef, ref);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clear = () => {
    setIsOpen(false);

    if (onChange) {
      if (variant === SelectVariantEnum.MULTI_SELECT) {
        onChange([]);
      } else {
        onChange(null);
      }
    }

    if (onClear) {
      onClear();
    }
  };

  const findOptionByValue = (value?: T | null) => {
    const availableOptions =
      withSearch && cachedSelectedOption
        ? [
            ...cachedSelectedOption,
            ...options.filter(
              (x) => !cachedSelectedOption.some((y) => y.value === x.value),
            ),
          ]
        : options;
    const option = availableOptions.find((x) => x.value === value);
    return option;
  };

  const getDefaultValueInput = () => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        return findOptionByValue(value[0])?.label;
      }
      return "";
    }

    return findOptionByValue(value)?.label;
  };

  const loadMore = async () => {
    if (loading || fetchingMore) return;
    if (!fetchMore) return;

    await fetchMore();
  };

  const handleScroll = async () => {
    if (
      !scrollOptionsRef.current ||
      !fetchMore ||
      loading ||
      fetchingMore ||
      !canLoadMore
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollOptionsRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      loadMore();
    }
  };

  const isMaxSelected =
    variant === SelectVariantEnum.MULTI_SELECT &&
    maxSelections !== undefined &&
    value &&
    value.length >= maxSelections;

  useEffect(() => {
    if (!dropdownFixed) return;

    let rafId: number;

    const getRect = () => {
      if (!inputRef.current) return null;
      return inputRef.current.getBoundingClientRect();
    };

    const DROPDOWN_INPUT_MARGIN = 2;
    const DROPDOWN_VIEWPORT_MARGIN = 8;

    // fit-content shrinks when the dropdown is anchored close to the right
    // edge, so it has to be measured with the whole viewport available.
    const getDropdownWidth = (rect: DOMRect) => {
      if (!optionsContainerFitContent || !dropdownRef.current) {
        return rect.width;
      }
      const previousLeft = dropdownRef.current.style.left;
      dropdownRef.current.style.left = "0px";
      const width = dropdownRef.current.offsetWidth;
      dropdownRef.current.style.left = previousLeft;
      return width;
    };

    const getLeft = (rect: DOMRect) => {
      const width = getDropdownWidth(rect);
      const overflowsRight =
        rect.left + width > window.innerWidth - DROPDOWN_VIEWPORT_MARGIN;
      // falls back to aligning the dropdown right edge with the input one
      const left = overflowsRight ? rect.right - width : rect.left;
      return Math.max(DROPDOWN_VIEWPORT_MARGIN, left);
    };

    const updatePosition = () => {
      const rect = getRect();
      if (!rect) return;
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < dropdownHeight;
      setPosition({
        top: flipUp
          ? rect.top - dropdownHeight - DROPDOWN_INPUT_MARGIN
          : rect.bottom + DROPDOWN_INPUT_MARGIN,
        left: getLeft(rect),
        width: rect.width,
        flipUp,
      });
    };

    const updatePositionDirect = () => {
      const rect = getRect();
      if (!rect || !dropdownRef.current) return;
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < dropdownHeight;
      dropdownRef.current.style.top = `${flipUp ? rect.top - dropdownHeight - DROPDOWN_INPUT_MARGIN : rect.bottom + DROPDOWN_INPUT_MARGIN}px`;
      dropdownRef.current.style.left = `${getLeft(rect)}px`;
      if (!optionsContainerFitContent) {
        dropdownRef.current.style.width = `${rect.width}px`;
      }
      setPosition((prev) =>
        prev.flipUp !== flipUp ? { ...prev, flipUp } : prev,
      );
    };

    const handleWindowScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePositionDirect);
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", handleWindowScroll, true);
      window.addEventListener("resize", handleWindowScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleWindowScroll, true);
      window.removeEventListener("resize", handleWindowScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isOpen, dropdownFixed, optionsContainerFitContent]);

  const selectDropdown = isOpen && (
    <StyledOptionsList
      ref={dropdownRef}
      $flipUp={position.flipUp}
      $fixed={dropdownFixed}
      onMouseDown={(e) => e.stopPropagation()}
      {...(id && { "data-testid": `${id}-dropdown` })}
      {...(dropdownFixed && {
        style: {
          top: position.top,
          left: position.left,
          width: optionsContainerFitContent ? "fit-content" : position.width,
          position: "fixed",
        },
      })}
    >
      <StyledOptionsScrollArea onScroll={handleScroll} ref={scrollOptionsRef}>
        {withSearch && (
          <StyledSearchInputContainer>
            <SearchInput
              value={searchValue}
              onChange={(value) => {
                if (onSearch) {
                  onSearch(value);
                }
              }}
              inputSize={InputSizes.SMALL}
              borderVariant={InputBorderEnum.BOTTOM}
            />
          </StyledSearchInputContainer>
        )}
        {options.map((option, index) => {
          const isDisabled =
            variant === SelectVariantEnum.MULTI_SELECT &&
            isMaxSelected &&
            value &&
            !value.includes(option.value);
          return (
            <StyledOptionItemContainer
              key={`select-option-${index}`}
              onClick={() => !isDisabled && handleOptionClick(option)}
            >
              {variant === SelectVariantEnum.DEFAULT && (
                <StyledOptionItem $variant={variant}>
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {option.label}
                  </BodyTyp>
                  {option.value === value && (
                    <IconManager
                      size={IconSizeEnum.SMALL}
                      color={theme.colors.purple}
                    >
                      <CheckIcon />
                    </IconManager>
                  )}
                </StyledOptionItem>
              )}
              {variant === SelectVariantEnum.SINGLE_SELECT && (
                <StyledOptionItem $variant={variant}>
                  <RadioButton value={option.value === value} />
                  <StyledOptionItemLabelContainer>
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {option.label}
                    </BodyTyp>
                    {option.description && (
                      <BodyTyp
                        variant="2"
                        fontWeight={FontWeightEnum.MEDIUM}
                        color={theme.colors.grey_3}
                      >
                        {option.description}
                      </BodyTyp>
                    )}
                  </StyledOptionItemLabelContainer>
                </StyledOptionItem>
              )}
              {variant === SelectVariantEnum.MULTI_SELECT && (
                <StyledOptionItem $variant={variant} $disabled={isDisabled}>
                  <Checkbox
                    value={value ? value.includes(option.value) : false}
                    disabled={isDisabled}
                  />
                  <BodyTyp
                    variant="2"
                    fontWeight={FontWeightEnum.MEDIUM}
                    color={
                      isDisabled ? theme.colors.grey_3 : theme.colors.black
                    }
                  >
                    {option.label}
                  </BodyTyp>
                </StyledOptionItem>
              )}
            </StyledOptionItemContainer>
          );
        })}
        {(loading || fetchingMore) && (
          <Loader loading={true} size={20}>
            <StyledLoaderEmptyContent />
          </Loader>
        )}
        {!loading && !fetchingMore && options.length === 0 && (
          <StyledOptionItemContainer>
            <StyledOptionItem $variant={SelectVariantEnum.DEFAULT} $disabled>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {NO_RESULT_FOUND_LABEL}
              </BodyTyp>
            </StyledOptionItem>
          </StyledOptionItemContainer>
        )}
      </StyledOptionsScrollArea>
    </StyledOptionsList>
  );

  return (
    <SelectContainer $size={size} ref={mergedRef} data-testid={id}>
      <StyledInputContainer
        ref={inputRef}
        {...(!disabled && { onClick: toggleOpen })}
      >
        <Input
          disabled={disabled}
          label={label}
          endIcon={
            <IconManager
              size={IconSizeEnum.SMALL}
              color={disabled ? theme.colors.grey_4 : theme.colors.black}
            >
              <ChevronDownIcon />
            </IconManager>
          }
          readOnly
          inputSize={size}
          onClear={clear}
          allowClear={allowClear}
          value={getDefaultValueInput()}
          {...(Array.isArray(value) &&
            value.length > 1 && {
              count: value.length - 1,
            })}
          error={error}
        />
      </StyledInputContainer>
      {dropdownFixed
        ? createPortal(selectDropdown, document.body)
        : selectDropdown}
    </SelectContainer>
  );
}

export const Select = forwardRef(SelectInner) as <T>(
  props: SelectProps<T> & React.RefAttributes<HTMLDivElement>,
) => ReturnType<typeof SelectInner>;
